/* eslint-disable statura/no-floating-point-money --
 * Aggregate cost estimator for roster pre-publish view. Sums per-shift
 * estimated pay using the Phase 1 calculator. Production calculation
 * (snapshotPayRun) is the canonical authority — this estimator is for
 * the manager's "what will this cost" preview only.
 */

/**
 * Phase 4 §8 (Component 19) — Roster pre-publish cost estimate.
 *
 * Pure aggregator. Given a list of shifts + the resolved
 * RateContext + classification per worker, returns a per-period
 * cost summary with per-facility / per-award breakdown.
 *
 * Used by:
 *  - /roster/periods/[id] cost panel (live recalc as shifts change)
 *  - BUDGET_BREACH_ESTIMATE gate (Component 19 — wires this output
 *    into the gate engine in a follow-up commit)
 *
 * Phase 1 deep-fix PR #5 (2026-05-17) — P1-D resolution. The function now
 * dispatches through `awardEngineFor(awardCode).calculateWithOvertime(...)`
 * for ALL awards (SCHADS via SchadsEngine drop-in delegate; non-SCHADS via
 * their per-engine implementations). Prior to PR #5, this estimator used
 * the OT-UNAWARE `engine.calculate(...)` base method on non-SCHADS engines
 * and a hand-rolled SCHADS legacy branch — both under-stated wages for
 * shifts exceeding daily-OT thresholds (Nurses 8h, AC/HPSS/SCHADS 10h) OR
 * for workers with weekly accumulation past 38h.
 *
 * The estimate now matches the canonical pay-run-actions snapshot path:
 * same engine method, same per-engine OT thresholds resolved via each
 * engine's `??` default, same per-worker-week ordinary-hours accumulator.
 *
 * Caller-side dispatch coherence: see
 * `project_caller_engine_dispatch_coherence.md`. This was the 5th confirmed
 * consumer of the coherence-invariant list (previously 4: preflight,
 * cumulative-hours, snapshot, recompute).
 */

import type { ShiftPayBreakdown, ShiftPayWithOvertimeInput } from './schads';
import type { BreakTreatmentSnapshotV1 } from './payroll/break-treatment';
import type { ActiveCertificationInput, CertGateRegistry } from './cert-currency';
import type { RateContext } from './award-rate-lookup';
import { awardEngineFor } from './award-engines';
import { DEFAULT_TZ, dateOnlyInTz } from './timezone';
import { compareInstants } from './roster-chronology';

export interface CostEstimateShift {
  id: string;
  staffId: string | null;
  facilityId: string | null;
  /**
   * Phase 1 deep-fix PR #6 (2026-05-18) — state-aware PH lookup. When set,
   * the cost estimator uses `facilityState` to resolve PH from the
   * caller-supplied `publicHolidaysByDate` map. When `facilityState` is
   * unresolved, only national-default rows (Set contains '') apply.
   */
  facilityState?: string | null;
  awardCode: string;
  schadsLevel: string;
  classificationStream: string | null;
  classificationLevel: string | null;
  classificationPayPoint: string | null;
  employmentType: 'casual' | 'permanent';
  startTime: string; // ISO
  endTime: string; // ISO
  breakMinutes: number;
  breakTreatment?: BreakTreatmentSnapshotV1 | null;
  /**
   * Manual PH override. Phase 1 deep-fix PR #6 — when false, the estimator
   * additionally consults `CostEstimateInput.publicHolidaysByDate` to
   * auto-detect declared state PHs. Manual `true` always wins.
   */
  isPublicHoliday: boolean;
  hasSleepover: boolean;
  brokenShiftCount: 0 | 1 | 2;
  travelKm: number | null | undefined;
  onCallWeekdayDays: number | null | undefined;
  onCallWeekendDays: number | null | undefined;
  hasDelayedMealAllowance: boolean;
  hasFirstAidResponsibility: boolean;
  hasUniformAllowance: boolean;
  hasLaundryAllowance: boolean;
  isFullTime: boolean;
  /**
   * Phase 1 deep-fix PR #5 (P1-D) — per-shift OT threshold gating. Mirrors
   * the snapshot-canonical input: full-time workers get the FT-initial-OT
   * tier per each award's clause (SCHADS cl 28.1 3h, etc.). If undefined,
   * falls back to `isFullTime`.
   */
  isFullTimeForOvertime?: boolean;
  activeCertifications?: readonly ActiveCertificationInput[];
  certGateRegistry?: CertGateRegistry;
  certGateAsOfDate?: string;
  /**
   * Phase 1 deep-fix PR #5 (P1-D) — shift date for per-worker-week
   * ordinary-hours accumulator key. ISO YYYY-MM-DD in the tenant's
   * timezone. When omitted, falls back to the calendar date of `startTime`
   * resolved through `CostEstimateInput.tz`. Canonical pay-run uses the
   * persisted tenant-tz timesheet `date` field.
   */
  shiftDate?: string;
  /**
   * Phase 1 deep-fix PR #2 (2026-05-17) — W4 / W4.0.5 / Phase 1O engine
   * inputs. Forward-looking estimate; defaults to null/false (no emission /
   * no lift / no carve-out) when caller doesn't supply, matching the
   * engine's pre-W4 behaviour. Authoritative wage outcome is set at
   * pay-run snapshot time when the coordinator has populated the timesheet
   * columns. See `apps/web/src/lib/payroll/pay-run-actions.ts` for the
   * canonical contract.
   */
  nauseousWorkHours?: number | null;
  leadingHandSupervisedCount?: number | null;
  mealProvidedDuringOT?: boolean;
  couldReturnHomeForMeal?: boolean;
}

export interface CostEstimateInput {
  shifts: CostEstimateShift[];
  /** RateContext per award code — caller pre-resolves all referenced awards. */
  rateContextByAward: Record<string, RateContext>;
  /** Optional period budget for delta computation. */
  periodBudget?: number;
  /** Optional prior-period total for delta-vs-prior. */
  priorPeriodTotal?: number;
  /**
   * Phase 1 deep-fix PR #6 (2026-05-18) — per-tenant public-holiday calendar
   * keyed by 'YYYY-MM-DD'. Value is the set of states that have a PH on
   * that date; empty string ('') represents a state-null (national-default)
   * row that applies across all states. Lookup hits when (facilityState in
   * set) OR ('' in set) OR (facilityState undefined AND set is non-empty).
   * When undefined, the estimator behaves exactly as pre-PR#6 (PH from
   * shift's manual override only). The web caller in apps/web populates
   * this from `loadPublicHolidaysForRange`.
   */
  publicHolidaysByDate?: Map<string, Set<string>>;
  /**
   * Phase 1 deep-fix PR #6 — IANA timezone for resolving shift dates to
   * tenant-local 'YYYY-MM-DD' keys. Required when `publicHolidaysByDate`
   * is supplied (otherwise the date key would not match the PH map).
   * Defaults to DEFAULT_TZ for backwards compatibility when callers omit tz.
   */
  tz?: string;
}

export interface CostEstimateResult {
  shiftCount: number;
  workerCount: number;
  unassignedShiftCount: number;
  /** Total estimated wages across all shifts. */
  totalWages: number;
  /** Sum of basePay across shifts. */
  totalBasePay: number;
  /** Sum of penaltyPay + shiftLoadingPay across shifts. */
  totalPenaltyPay: number;
  /** Sum of overtimePay across shifts (Phase 1 deep-fix PR #5 — P1-D). */
  totalOvertimePay: number;
  /** Sum of allowancesPay across shifts. */
  totalAllowances: number;
  /** Per-facility breakdown. */
  byFacility: Array<{
    facilityId: string | null;
    shiftCount: number;
    totalWages: number;
  }>;
  /** Per-award breakdown. */
  byAward: Array<{ awardCode: string; shiftCount: number; totalWages: number }>;
  /** Delta vs prior period (positive = more this period). Null if no prior. */
  priorPeriodDelta: number | null;
  /** Variance vs budget (positive = over budget). Null if no budget. */
  budgetVariance: number | null;
  /** Whether the period exceeds its budget by more than 5% (configurable in caller). */
  isBudgetBreach: boolean;
  /**
   * Phase 1 deep-fix PR #5 (P1-D + P1-E) — fail-loud accumulator. Lists
   * award codes encountered that have no engine implementation
   * (`UnsupportedAwardEngine` returns $0). Callers MUST surface these to
   * the user; without explicit handling the forecast would silently
   * under-state the cost.
   */
  unsupportedAwards: string[];
  /** Per-shift details for drill-down. Includes warnings from the calculator. */
  perShift: Array<{
    shiftId: string;
    staffId: string | null;
    awardCode: string;
    estimatedTotal: number;
    warnings: string[];
  }>;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Phase 1 deep-fix PR #5 (P1-D) — ISO-Monday key for the per-(staff,
 * week) ordinary-hours accumulator. Mirrors
 * `apps/web/src/lib/payroll/pay-run-actions.ts:isoMondayKey` byte-for-byte.
 *
 * Input is YYYY-MM-DD in the tenant's timezone; parsed as UTC-midnight so
 * `getUTCDay()` returns the calendar weekday with no tz drift. Returns
 * the YYYY-MM-DD of the ISO Monday of that week (Mon=1 ... Sun=7).
 */
function isoMondayKey(tenantTzDate: string): string {
  const d = new Date(`${tenantTzDate}T00:00:00Z`);
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  // eslint-disable-next-line statura/no-utc-date-string -- date now points at UTC-midnight of ISO Monday; toISOString().slice(0,10) extracts YYYY-MM-DD with no tz drift
  return d.toISOString().slice(0, 10);
}

function shiftDateKey(shift: CostEstimateShift, tz: string = DEFAULT_TZ): string {
  if (shift.shiftDate) return shift.shiftDate;
  const parsed = new Date(shift.startTime);
  if (Number.isFinite(parsed.getTime())) return dateOnlyInTz(parsed, tz);
  return shift.startTime.slice(0, 10);
}

function computeShift(
  shift: CostEstimateShift,
  ctx: RateContext | undefined,
  priorOrdinaryHours: number,
  effectiveIsPublicHoliday: boolean,
  shiftDate: string,
): ShiftPayBreakdown | null {
  if (!shift.staffId) return null; // unassigned — no estimate
  if (!ctx) return null; // no rate context — caller may have failed lookup

  // Phase 1 deep-fix PR #5 (P1-D) — UNIFIED engine dispatch matching
  // pay-run-actions.ts:793 canonical pattern. All four engines implement
  // `calculateWithOvertime(input, ctx)` uniformly. SchadsEngine is a
  // drop-in delegate to `legacyCalculateShiftPayWithOvertime` — verified
  // packages/utils/src/award-engines/schads.ts:71-75; dual-run.test.ts
  // 49/49 byte-equal regression net pins SCHADS math equivalence.
  const engine = awardEngineFor(shift.awardCode);

  const otInput = {
    schadsLevel: shift.schadsLevel,
    employmentType: shift.employmentType,
    employeeCategory: 'dayworker' as const,
    startTime: new Date(shift.startTime),
    endTime: new Date(shift.endTime),
    breakMinutes: shift.breakMinutes,
    breakTreatment: shift.breakTreatment ?? null,
    isPublicHoliday: effectiveIsPublicHoliday,
    hasSleepover: shift.hasSleepover,
    hasBrokenShift: shift.brokenShiftCount,
    travelKm: shift.travelKm ?? 0,
    onCallWeekdayDays: shift.onCallWeekdayDays ?? 0,
    onCallWeekendDays: shift.onCallWeekendDays ?? 0,
    hasDelayedMealAllowance: shift.hasDelayedMealAllowance,
    hasFirstAidResponsibility: shift.hasFirstAidResponsibility,
    hasUniformAllowance: shift.hasUniformAllowance,
    hasLaundryAllowance: shift.hasLaundryAllowance,
    isFullTime: shift.isFullTime,
    ordinaryHoursWorkedEarlierThisWeek: priorOrdinaryHours,
    // Explicit 38h weekly for auditability (matches every engine's default).
    ordinaryHoursLimitPerWeek: 38,
    // ordinaryHoursLimitPerShift omitted — each engine applies its own
    // primary-clause default (Nurses cl 19 8h, AC cl 25 10h, HPSS cl 13.1(b)
    // 10h, SCHADS cl 28 10h). Single source of truth in the engine.
    isFullTimeForOvertime: shift.isFullTimeForOvertime ?? shift.isFullTime,
    activeCertifications: shift.activeCertifications,
    certGateRegistry: shift.certGateRegistry,
    certGateAsOfDate: shift.certGateAsOfDate ?? shiftDate,
    // Phase 1D duck-typed extras for non-SCHADS engines (Nurses/AC/HPSS
    // consume; SCHADS legacy ignores forward-compat). The base interface
    // `ShiftPayWithOvertimeInput` doesn't declare these — each engine
    // narrows via its own `<Engine>InputExtras` cast (see
    // packages/utils/src/award-engines/nurses.ts:1258).
    classificationStream: shift.classificationStream,
    classificationLevel: shift.classificationLevel ?? undefined,
    classificationPayPoint: shift.classificationPayPoint ?? null,
    // Phase 1 deep-fix PR #2 (2026-05-17) — W4 / W4.0.5 / Phase 1O engine
    // inputs. Default-null / default-false matches pre-W4 engine behaviour
    // for the forward-looking estimate.
    nauseousWorkHours: shift.nauseousWorkHours ?? null,
    leadingHandSupervisedCount: shift.leadingHandSupervisedCount ?? null,
    mealProvidedDuringOT: shift.mealProvidedDuringOT ?? false,
    couldReturnHomeForMeal: shift.couldReturnHomeForMeal ?? false,
  } as ShiftPayWithOvertimeInput;

  return engine.calculateWithOvertime(otInput, ctx);
}

export function estimateRosterCost(input: CostEstimateInput): CostEstimateResult {
  const perShift: CostEstimateResult['perShift'] = [];
  let totalWages = 0;
  let totalBasePay = 0;
  let totalPenaltyPay = 0;
  let totalOvertimePay = 0;
  let totalAllowances = 0;
  const byFacility = new Map<string | null, { shiftCount: number; totalWages: number }>();
  const byAward = new Map<string, { shiftCount: number; totalWages: number }>();
  const workerSet = new Set<string>();
  const unsupportedAwardsSet = new Set<string>();
  let unassignedShiftCount = 0;

  // Phase 1 deep-fix PR #5 (P1-D) — per-(staff, ISO-week) ordinary-hours
  // accumulator. Mirrors pay-run-actions.ts:645 + cumulative-hours.ts:135-170.
  // Sort shifts chronologically so the accumulator sees them in the order a
  // worker accrues hours within the week. Ordered by parsed instant rather than
  // by string: mixed ISO forms of the same instant (`...Z` vs `...+00:00`) sort
  // wrongly as text, which would feed the ordinary/overtime split out of order.
  const sortedShifts = [...input.shifts].sort((a, b) => compareInstants(a.startTime, b.startTime));
  const priorOrdinaryByStaffWeek = new Map<string, number>();
  const tz = input.tz ?? DEFAULT_TZ;

  for (const shift of sortedShifts) {
    if (!shift.staffId) {
      unassignedShiftCount++;
      perShift.push({
        shiftId: shift.id,
        staffId: null,
        awardCode: shift.awardCode,
        estimatedTotal: 0,
        warnings: ['Shift unassigned — no cost estimate'],
      });
      continue;
    }
    workerSet.add(shift.staffId);

    const shiftDate = shiftDateKey(shift, tz);
    const weekKey = `${shift.staffId}|${isoMondayKey(shiftDate)}`;
    const priorOrdinary = priorOrdinaryByStaffWeek.get(weekKey) ?? 0;

    // Phase 1 deep-fix PR #6 — state-aware PH lookup. Manual override
    // (shift.isPublicHoliday) wins. When caller supplied a calendar map,
    // we auto-detect PH for shifts whose tenant-tz date matches an entry
    // whose state set covers the shift's facility state (or contains '' for
    // a national-default row). Unresolved facilityState matches national only.
    let effectivePh = shift.isPublicHoliday;
    if (!effectivePh && input.publicHolidaysByDate) {
      const dateKey = dateOnlyInTz(new Date(shift.startTime), tz);
      const set = input.publicHolidaysByDate.get(dateKey);
      if (set) {
        const facState = shift.facilityState ?? null;
        if (facState && set.has(facState)) effectivePh = true;
        else if (set.has('')) effectivePh = true;
        // Unresolved state: national defaults only ('' token), never any-state union.
      }
    }

    const ctx = input.rateContextByAward[shift.awardCode];
    const breakdown = computeShift(shift, ctx, priorOrdinary, effectivePh, shiftDate);
    if (!breakdown) {
      perShift.push({
        shiftId: shift.id,
        staffId: shift.staffId,
        awardCode: shift.awardCode,
        estimatedTotal: 0,
        warnings: [`No rate context for award ${shift.awardCode}`],
      });
      continue;
    }

    // Phase 1 deep-fix PR #5 (P1-D + P1-E) — fail-loud on UnsupportedAwardEngine.
    // The engine returns a breakdown with a warning prefixed
    // `Award <code> is not yet supported` and $0 across the board (see
    // award-engines/index.ts:107). We surface the original code via the
    // shift's awardCode (the engine's awardCode is a 'MA000100' placeholder).
    const unsupportedWarning = breakdown.warnings.find((w) => w.includes('is not yet supported'));
    if (unsupportedWarning) {
      unsupportedAwardsSet.add(shift.awardCode);
    }

    totalWages += breakdown.grossTotal;
    totalBasePay += breakdown.basePay;
    totalPenaltyPay += breakdown.penaltyPay + breakdown.shiftLoadingPay;
    totalOvertimePay += breakdown.overtimePay;
    totalAllowances += breakdown.allowancesPay;

    // Phase 1 deep-fix PR #5 (P1-D) — track ordinary hours accrued this
    // shift (excluding OT) so the NEXT shift in the same week sees the
    // cumulative tally. Same pattern as pay-run-actions.ts:794-800.
    const ordinaryHoursThisShift =
      breakdown.bands.weekdayOrdinary +
      breakdown.bands.saturdayOrdinary +
      breakdown.bands.sundayOrdinary +
      breakdown.bands.publicHoliday +
      breakdown.bands.eveningShift +
      breakdown.bands.nightShift;
    priorOrdinaryByStaffWeek.set(weekKey, priorOrdinary + ordinaryHoursThisShift);

    const fac = byFacility.get(shift.facilityId) ?? { shiftCount: 0, totalWages: 0 };
    fac.shiftCount += 1;
    fac.totalWages += breakdown.grossTotal;
    byFacility.set(shift.facilityId, fac);

    const aw = byAward.get(shift.awardCode) ?? { shiftCount: 0, totalWages: 0 };
    aw.shiftCount += 1;
    aw.totalWages += breakdown.grossTotal;
    byAward.set(shift.awardCode, aw);

    perShift.push({
      shiftId: shift.id,
      staffId: shift.staffId,
      awardCode: shift.awardCode,
      estimatedTotal: breakdown.grossTotal,
      warnings: breakdown.warnings,
    });
  }

  const priorPeriodDelta =
    input.priorPeriodTotal != null ? round2(totalWages - input.priorPeriodTotal) : null;
  const budgetVariance =
    input.periodBudget != null ? round2(totalWages - input.periodBudget) : null;
  const isBudgetBreach =
    input.periodBudget != null
      ? totalWages > input.periodBudget * 1.05 // configurable tolerance per tenant in follow-up
      : false;

  return {
    shiftCount: input.shifts.length,
    workerCount: workerSet.size,
    unassignedShiftCount,
    totalWages: round2(totalWages),
    totalBasePay: round2(totalBasePay),
    totalPenaltyPay: round2(totalPenaltyPay),
    totalOvertimePay: round2(totalOvertimePay),
    totalAllowances: round2(totalAllowances),
    byFacility: Array.from(byFacility.entries())
      .map(([facilityId, v]) => ({
        facilityId,
        shiftCount: v.shiftCount,
        totalWages: round2(v.totalWages),
      }))
      .sort((a, b) => b.totalWages - a.totalWages),
    byAward: Array.from(byAward.entries())
      .map(([awardCode, v]) => ({
        awardCode,
        shiftCount: v.shiftCount,
        totalWages: round2(v.totalWages),
      }))
      .sort((a, b) => b.totalWages - a.totalWages),
    priorPeriodDelta,
    budgetVariance,
    isBudgetBreach,
    unsupportedAwards: Array.from(unsupportedAwardsSet).sort(),
    perShift,
  };
}
